import { useMemo, useRef, useState } from 'react';
import { Button, Card, Empty, Space, Spin, Tag, Timeline, Typography, message } from 'antd';
import { Link, useParams } from 'react-router-dom';
import {
  IconAlertOctagon,
  IconFileText,
  IconPaperclip,
  IconUpload,
  IconRefresh,
  IconPlayerPlay,
} from '@tabler/icons-react';

import { useAuthSession } from '@/entities/auth/api/hooks';
import { useAssignmentDetail, useAssignmentEvents } from '@/entities/training/api/libraryApi';
import { RecordTrainingDrawer } from '../RecordTrainingDrawer';
import {
  assignmentStatusLabels,
  programKindLabels,
  type AssignmentEvent,
  type AssignmentStatus,
  type ProgramKind,
} from '@/entities/training/model/librarySchema';

import { trainingAssignmentStatusTagColor } from '@/entities/training/model/trainingTableTagColors';
import { RecordCommentsSection, type RecordCommentItem } from '@/shared/ui/RecordCommentsSection';

import s from './TrainingRecordDetail.module.scss';

const { Text } = Typography;

const CRITICAL_STATUSES = new Set(['overdue', 'not_passed', 'cancelled']);

const EVENT_TYPE_LABELS: Record<string, string> = {
  status_changed: 'Изменение статуса',
  attempt_recorded: 'Попытка прохождения',
  document_uploaded: 'Загружен документ',
  comment_added: 'Комментарий',
  responsible_changed: 'Изменён ответственный',
};

const REASON_LABELS: Record<string, string> = {
  hire: 'Приём на работу',
  matrix: 'По матрице обучения',
  manual: 'Назначено вручную',
  unplanned_trigger: 'Внеплановый триггер',
};

type JournalFilter = 'all' | 'events' | 'documents' | 'changes';

const JOURNAL_CHIPS: { key: JournalFilter; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'events', label: 'События' },
  { key: 'documents', label: 'Документы' },
  { key: 'changes', label: 'Изменения' },
];

const FILTER_EVENT_TYPES: Record<JournalFilter, Set<string> | null> = {
  all: null,
  events: new Set(['status_changed', 'attempt_recorded']),
  documents: new Set(['document_uploaded']),
  changes: new Set(['responsible_changed', 'status_changed']),
};

function EventItem({ event }: { event: AssignmentEvent }) {
  const statusLabel = event.new_value?.status
    ? (assignmentStatusLabels[event.new_value.status as AssignmentStatus] ?? event.new_value.status)
    : null;

  const isComment = event.event_type === 'comment_added';
  const isDoc = event.event_type === 'document_uploaded';

  return (
    <>
      <Text type="secondary" className={s['root__activity-time']}>
        {new Date(event.changed_at).toLocaleString('ru-RU')}
      </Text>
      <div className={s['root__activity-event']}>
        {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
        {statusLabel ? ` → ${statusLabel}` : null}
      </div>
      {isComment && event.reason ? <div className={s['root__commentBody']}>{event.reason}</div> : null}
      {isDoc ? (
        <>
          {event.new_value?.name ? (
            <div className={s['root__activity-file']}>
              <IconFileText size={14} />
              {event.new_value.name}
            </div>
          ) : null}
          {event.reason ? (
            <Text type="secondary" className={s['root__activity-author']}>
              {event.reason}
            </Text>
          ) : null}
        </>
      ) : null}
      {event.changed_by ? (
        <Text type="secondary" className={s['root__activity-author']}>
          {event.changed_by}
        </Text>
      ) : null}
      {!isComment && !isDoc && event.reason ? (
        <Text type="secondary" className={s['root__activity-author']}>
          {event.reason}
        </Text>
      ) : null}
    </>
  );
}

export function TrainingRecordDetail() {
  const { recordId } = useParams<{ recordId: string }>();
  const { data: assignment, isLoading } = useAssignmentDetail(recordId ?? null);
  const { data: events } = useAssignmentEvents(recordId ?? null);
  const { data: auth } = useAuthSession();
  const [journalFilter, setJournalFilter] = useState<JournalFilter>('all');
  const [recordDrawerOpen, setRecordDrawerOpen] = useState(false);
  const documentsFileInputRef = useRef<HTMLInputElement>(null);
  const commentsFileInputRef = useRef<HTMLInputElement>(null);

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    const allowed = FILTER_EVENT_TYPES[journalFilter];
    if (!allowed) return events;
    return events.filter((e) => allowed.has(e.event_type));
  }, [events, journalFilter]);

  const documentEvents = useMemo(
    () =>
      [...(events ?? [])]
        .filter((e) => e.event_type === 'document_uploaded')
        .sort((a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime()),
    [events],
  );

  const commentEvents = useMemo(
    () =>
      [...(events ?? [])]
        .filter((e) => e.event_type === 'comment_added')
        .sort((a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime()),
    [events],
  );

  const commentItems: RecordCommentItem[] = useMemo(
    () =>
      commentEvents
        .filter((e) => (e.reason ?? '').trim().length > 0)
        .map((e) => ({
          id: e.id,
          authorName: e.changed_by?.trim() || 'Участник',
          body: e.reason ?? '',
          createdAt: e.changed_at,
        })),
    [commentEvents],
  );

  const triggerDemoFilePick = (which: 'documents' | 'comments') => {
    message.info('В демо режиме файлы на сервер не загружаются. Выберите файл, чтобы проверить выбор.');
    (which === 'documents' ? documentsFileInputRef : commentsFileInputRef).current?.click();
  };

  const onDemoFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) message.success(`Выбран файл: ${f.name} (не сохранён в демо)`);
    e.target.value = '';
  };

  if (!recordId) {
    return <Empty description="Некорректный идентификатор записи" />;
  }

  const isCritical = assignment ? CRITICAL_STATUSES.has(assignment.status) : false;
  const isCancelled = assignment?.status === 'cancelled';

  return (
    <Spin spinning={isLoading}>
      {!isLoading && !assignment ? (
        <Empty description="Запись не найдена" />
      ) : assignment ? (
        <div className={s.root}>
          <div className={s.root__breadcrumbs}>
            <Link to="/training?tab=records" className={s.root__crumbLink}>
              ← К списку сотрудников
            </Link>
            <span className={s.root__crumbSep}>/</span>
            <Link to={`/training/employee/${assignment.employee_id}`} className={s.root__crumbLink}>
              {assignment.employee_name}
            </Link>
            <span className={s.root__crumbSep}>/</span>
            <span className={s.root__crumbCurrent}>{assignment.program.name}</span>
          </div>

          <div className={s.root__hero}>
            <div className={s.root__heroInfo}>
              <div className={s.root__heroLabel}>Обучение</div>
              <div className={s.root__heroName}>{assignment.program.name}</div>
              <div className={s.root__heroMeta}>
                <Link
                  to={`/employees?employeeId=${assignment.employee_id}&tab=training`}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  {assignment.employee_name}
                </Link>{' '}
                · {assignment.department_name}
              </div>
              <div className={s.root__heroBadges}>
                <Tag
                  color={trainingAssignmentStatusTagColor[assignment.status] ?? 'default'}
                  style={{ textAlign: 'center' }}
                  className={s.root__statusTag}
                >
                  {assignmentStatusLabels[assignment.status as AssignmentStatus] ?? assignment.status}
                </Tag>
                {assignment.program.program_kind ? (
                  <Tag className={s.root__heroGhostTag}>
                    {programKindLabels[assignment.program.program_kind as ProgramKind] ??
                      assignment.program.program_kind}
                  </Tag>
                ) : null}
                {assignment.level ? <Tag className={s.root__heroGhostTag}>{assignment.level.level_name}</Tag> : null}
                {assignment.program.is_level_based ? <Tag className={s.root__heroGhostTag}>Обязательная</Tag> : null}
              </div>
            </div>
          </div>

          {isCritical ? (
            <div className={s.root__problemAlert}>
              <IconAlertOctagon size={20} className={s.root__problemIcon} />
              <div className={s.root__problemContent}>
                <div className={s.root__problemTitle}>
                  {assignment.status === 'overdue'
                    ? 'Обучение просрочено'
                    : assignment.status === 'not_passed'
                      ? 'Обучение не пройдено'
                      : 'Назначение отозвано'}
                </div>
                <div className={s.root__problemDesc}>
                  {assignment.status === 'overdue'
                    ? 'Срок прохождения обучения истёк. Необходимо переназначить или зафиксировать результат.'
                    : assignment.status === 'not_passed'
                      ? 'Итоговая проверка не сдана. Назначьте пересдачу или обратитесь к ответственному.'
                      : 'Назначение было отозвано. При необходимости создайте новое.'}
                </div>
                <Space style={{ marginTop: 8 }}>
                  {assignment.status === 'overdue' ? (
                    <>
                      <Button size="small" type="primary" icon={<IconRefresh size={14} />} disabled>
                        Переназначить
                      </Button>
                      <Button size="small" icon={<IconFileText size={14} />} disabled>
                        Внести результат
                      </Button>
                    </>
                  ) : assignment.status === 'not_passed' ? (
                    <Button size="small" type="primary" icon={<IconPlayerPlay size={14} />} disabled>
                      Назначить пересдачу
                    </Button>
                  ) : (
                    <Button size="small" type="primary" icon={<IconRefresh size={14} />} disabled>
                      Назначить заново
                    </Button>
                  )}
                </Space>
              </div>
            </div>
          ) : null}

          <Card size="small" className={s.root__section}>
            <div className={s.root__sectionTitle}>Сведения об обучении</div>
            <div className={s.root__detailsGrid}>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Программа</div>
                <div className={s.root__detailValue}>{assignment.program.name}</div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Основание</div>
                <div className={s.root__detailValue}>
                  {REASON_LABELS[assignment.assignment_reason] ?? assignment.assignment_reason}
                </div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Дата назначения</div>
                <div className={s.root__detailValue}>{assignment.planned_at ?? '—'}</div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Срок прохождения</div>
                <div className={s.root__detailValue}>{assignment.due_at ?? '—'}</div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Дата прохождения</div>
                <div className={s.root__detailValue}>{assignment.completed_at ?? '—'}</div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Действует до</div>
                <div className={s.root__detailValue}>{assignment.valid_until ?? '—'}</div>
              </div>
            </div>
          </Card>

          <Card size="small" className={s.root__section}>
            <div className={s.root__sectionHeader}>
              <div className={s.root__sectionTitle}>Журнал обучения</div>
              <Space size={4}>
                {JOURNAL_CHIPS.map((chip) => (
                  <Tag
                    key={chip.key}
                    className={`${s.root__journalChip} ${journalFilter === chip.key ? s.root__journalChip_active : ''}`}
                    onClick={() => setJournalFilter(chip.key)}
                  >
                    {chip.label}
                  </Tag>
                ))}
              </Space>
            </div>
            {filteredEvents.length > 0 ? (
              <Timeline
                className={s.root__timeline}
                items={filteredEvents.map((e) => ({
                  key: e.id,
                  color:
                    e.new_value?.status === 'passed'
                      ? 'green'
                      : CRITICAL_STATUSES.has(String(e.new_value?.status ?? ''))
                        ? 'red'
                        : 'blue',
                  children: <EventItem event={e} />,
                }))}
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {events && events.length > 0 ? 'Нет событий по выбранному фильтру' : 'Событий пока нет'}
              </Text>
            )}
          </Card>

          <Card size="small" className={s.root__section}>
            <input
              ref={documentsFileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              hidden
              onChange={onDemoFileChosen}
            />
            <div className={s.root__sectionHeader}>
              <div className={s.root__sectionTitle}>Документы</div>
              <Button
                size="small"
                icon={<IconUpload size={14} />}
                disabled={isCancelled}
                onClick={() => !isCancelled && triggerDemoFilePick('documents')}
              >
                Прикрепить
              </Button>
            </div>
            {documentEvents.length > 0 ? (
              <ul className={s.root__docList}>
                {documentEvents.map((e) => (
                  <li key={e.id} className={s.root__docRow}>
                    <IconFileText size={16} className={s.root__docIcon} />
                    <div>
                      <div className={s.root__docName}>{e.new_value?.name ?? 'Документ'}</div>
                      {e.reason ? (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {e.reason}
                        </Text>
                      ) : null}
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        {e.changed_by ?? ''} · {new Date(e.changed_at).toLocaleString('ru-RU')}
                      </Text>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={s.root__emptyBlock}>
                <IconPaperclip size={20} style={{ color: 'var(--n400)' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Документы появятся по ходу обучения
                </Text>
              </div>
            )}
          </Card>

          <Card size="small" className={s.root__section}>
            <input
              ref={commentsFileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              hidden
              onChange={onDemoFileChosen}
            />
            <RecordCommentsSection
              items={commentItems}
              composerDisplayName={auth?.user.display_name}
              showComposer={!isCancelled}
              onAttach={() => triggerDemoFilePick('comments')}
              onMention={() => message.info('Упоминания будут доступны после подключения к серверу.')}
            />
          </Card>

          <div className={s.root__footer}>
            <Button
              type="primary"
              icon={<IconFileText size={14} />}
              onClick={() => setRecordDrawerOpen(true)}
              style={{ marginRight: 12 }}
              disabled
            >
              Внести запись о пройденном
            </Button>
          </div>

          <RecordTrainingDrawer
            open={recordDrawerOpen}
            onClose={() => setRecordDrawerOpen(false)}
            prefilledEmployee={assignment.employee_name}
            prefilledProgram={assignment.program.program_kind ?? undefined}
          />
        </div>
      ) : null}
    </Spin>
  );
}
